import { landmineNames } from '@mm/constants/landmine-names';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import rightArrow from 'assets/icons/right-arrow.svg';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { ConditionalLogicModeProps } from '../ConditionalLogic';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PlanBadge } from '../../../../../../../common/components/ProBadge';

export const ConditionalLogicRoot = ({
  editWidget,
  setEditMode,
  conditionalTags,
  conditionalFilters,
  conditionalAudiences,
  variables,
  audiences,
  changesPending,
  onUpdateConditionalLogic,
  onClose,
  loadingAudienceVariables,
  setEditFilter,
}: ConditionalLogicModeProps) => {
  const [saving, setSaving] = useState(false);

  const canUseConditionalLogic = useSelector(
    combinedFeatureFlagsSelector,
  ).botBuilderConditionalLogic;

  const modalActions = useModalActions();

  const onApplyChanges = async () => {
    setSaving(true);
    try {
      if (!canUseConditionalLogic) {
        return modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['WIDGETS__CONDITIONAL-LOGIC'],
          feature: 'botBuilderConditionalLogic',
        });
      }
      await onUpdateConditionalLogic(editWidget);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PlanBadge
        className="widget-conditional-logic__pro-ribbon"
        billingType={PageBillingType.Pro}
      />
      <div className="widget-conditional-logic__header-text">
        Conditional Logic Settings
      </div>
      <div className="widget-conditional-logic__instructions">
        Show this widget only to contacts that meet ALL the following criteria:
      </div>
      <Button
        text
        className="widget-conditional-logic__item-row"
        onClick={() => setEditMode('tags')}
      >
        <div className="widget-conditional-logic__item-header">Tags</div>
        <div className="widget-conditional-logic__item-content">
          {conditionalTags.length === 0
            ? 'No tags chosen'
            : conditionalTags.map((t, i) => (
                <>
                  {i > 0 && (
                    <div className="widget-conditional-logic__item-content-item-separator">
                      OR
                    </div>
                  )}
                  <div
                    key={t.tagName}
                    className="widget-conditional-logic__item-content-item"
                  >
                    {t.tagName}
                  </div>
                </>
              ))}
        </div>
        <img
          className="widget-conditional-logic__right-arrow"
          alt=""
          src={rightArrow}
        />
      </Button>
      <Button
        text
        className="widget-conditional-logic__item-row"
        onClick={() => setEditMode('attributes')}
      >
        <div className="widget-conditional-logic__item-header">Attributes</div>
        <div className="widget-conditional-logic__item-content">
          {loadingAudienceVariables ? (
            <span>
              <i className="fas fa-spinner fa-spin" /> Loading attributes...
            </span>
          ) : (
            <>
              {conditionalFilters.length === 0
                ? 'No attributes chosen'
                : conditionalFilters.map((f, i) => (
                    <>
                      {i > 0 && (
                        <div className="widget-conditional-logic__item-content-item-separator">
                          AND
                        </div>
                      )}
                      <Button
                        text
                        key={f.id || JSON.stringify(f)}
                        className="widget-conditional-logic__item-content-item"
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          setEditFilter(f);
                          setEditMode('attribute-detail');
                        }}
                      >
                        {get(
                          variables.find(
                            v =>
                              v.variableId === f.variableId &&
                              v.variableType === f.variableType,
                          ),
                          'name',
                        )}
                      </Button>
                    </>
                  ))}
            </>
          )}
        </div>
        <img
          className="widget-conditional-logic__right-arrow"
          alt=""
          src={rightArrow}
        />
      </Button>
      <Button
        text
        className="widget-conditional-logic__item-row"
        onClick={() => setEditMode('audiences')}
      >
        <div className="widget-conditional-logic__item-header">Audiences</div>
        <div className="widget-conditional-logic__item-content">
          {conditionalAudiences.length === 0
            ? 'No audiences chosen'
            : conditionalAudiences.map((ca, i) => (
                <>
                  {i > 0 && (
                    <div className="widget-conditional-logic__item-content-item-separator">
                      OR
                    </div>
                  )}
                  <div
                    key={ca.audienceId}
                    className="widget-conditional-logic__item-content-item"
                  >
                    {get(
                      audiences.find(a => a.id === ca.audienceId),
                      'name',
                    )}
                  </div>
                </>
              ))}
        </div>
        <img
          className="widget-conditional-logic__right-arrow"
          alt=""
          src={rightArrow}
        />
      </Button>
      {(saving || changesPending) && (
        <PrimaryButton
          disabled={saving}
          css={{ container: 'widget-conditional-logic__save-button' }}
          onClick={onApplyChanges}
        >
          Apply changes
        </PrimaryButton>
      )}
    </>
  );
};
